import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { BusinessPlan } from '../../../store/useTenantStore';
import {
  useAdminTenantStore,
  type CreateTenantResponse,
} from '../../../store/useAdminTenantStore';
import { useToastStore } from '../../../store/useToastStore';
import { Button } from '../../../components/Shared/Button';
import { Input } from '../../../components/Shared/Input';
import { FormField } from '../../../components/Shared/FormField';
import type { FacilityFlowCoreModulesTenantsDtosTenantDto } from '../../../api/generated/models';

interface TenantFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  tenant: FacilityFlowCoreModulesTenantsDtosTenantDto | null;
  onCreated?: (response: CreateTenantResponse) => void;
}

export default function TenantFormDialog({ isOpen, onClose, tenant, onCreated }: TenantFormDialogProps) {
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [businessPlan, setBusinessPlan] = useState<number>(0);
  const [street, setStreet] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isActive, setIsActive] = useState(true);

  const { create, update, saving } = useAdminTenantStore();
  const { addToast } = useToastStore();

  useEffect(() => {
    if (tenant) {
      setName(tenant.name || '');
      setDomain(tenant.domain || '');
      setBusinessPlan(tenant.businessPlan || 0);
      setStreet(tenant.address?.street || '');
      setStreetNumber(tenant.address?.streetNumber || '');
      setCity(tenant.address?.city || '');
      setPostalCode(tenant.address?.postalCode || '');
      setCountry(tenant.address?.country || '');
      setFirstName(tenant.contactPerson?.firstName || '');
      setLastName(tenant.contactPerson?.lastName || '');
      setEmail(tenant.contactPerson?.email || '');
      setPhone(tenant.contactPerson?.phone || '');
      setIsActive(tenant.isActive ?? true);
    } else {
      resetForm();
    }
  }, [tenant]);

  const resetForm = () => {
    setName('');
    setDomain('');
    setBusinessPlan(0);
    setStreet('');
    setStreetNumber('');
    setCity('');
    setPostalCode('');
    setCountry('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setIsActive(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data = {
      name,
      domain,
      businessPlan: businessPlan as BusinessPlan,
      address: {
        street,
        streetNumber,
        city,
        postalCode,
        country,
      },
      contactPerson: {
        firstName,
        lastName,
        email,
        phone,
      },
      isActive,
    };

    if (tenant?.id) {
      const ok = await update(tenant.id, data);
      if (ok) {
        addToast('Mandant aktualisiert', 'success');
        onClose();
        resetForm();
      }
    } else {
      const response = await create(data);
      if (response) {
        onClose();
        resetForm();
        // Parent surfaces the welcome credentials dialog; falls back to a
        // toast if no handler is wired (e.g. legacy callsite).
        if (onCreated) {
          onCreated(response);
        } else {
          addToast('Mandant erstellt', 'success');
        }
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {tenant ? 'Tenant bearbeiten' : 'Neuer Tenant'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              aria-label="Dialog schließen"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Basis-Informationen
              </h3>
              
              <FormField label="Name" required>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </FormField>

              <FormField label="Domain">
                <Input
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="z.B. tenant1"
                />
              </FormField>

              <FormField label="Business Plan" required>
                <select
                  value={businessPlan}
                  onChange={(e) => setBusinessPlan(Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                  aria-label="Business Plan auswählen"
                >
                  <option value={0}>Free</option>
                  <option value={1}>Basic</option>
                  <option value={2}>Professional</option>
                  <option value={3}>Enterprise</option>
                </select>
              </FormField>

              <FormField label="Status">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Aktiv</span>
                </label>
              </FormField>
            </div>

            {/* Address */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Adresse</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Straße">
                  <Input
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                  />
                </FormField>

                <FormField label="Hausnummer">
                  <Input
                    value={streetNumber}
                    onChange={(e) => setStreetNumber(e.target.value)}
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField label="Postleitzahl">
                  <Input
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                  />
                </FormField>

                <FormField label="Stadt">
                  <Input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </FormField>
              </div>

              <FormField label="Land">
                <Input
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
              </FormField>
            </div>

            {/* Contact Person */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Kontaktperson
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Vorname">
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </FormField>

                <FormField label="Nachname">
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </FormField>
              </div>

              <FormField label="E-Mail">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </FormField>

              <FormField label="Telefon">
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </FormField>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button type="button" variant="outline" onClick={onClose}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Wird gespeichert...' : tenant ? 'Aktualisieren' : 'Erstellen'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
